/**
 * Created by Rodrigo Quesada on 16/01/17.
 */
import {Injectable} from '@angular/core';
import {Observable, ReplaySubject, BehaviorSubject} from 'rxjs';
import {ActivatedRouteSnapshot} from '@angular/router';
import {BoardAffiliationInformation} from '../../../../domain/model/licenses-certifications/board/information/board-affiliation-information.model';
import {BoardAffiliationInformationService} from '../../../../domain/model/licenses-certifications/board/information/board-affiliation-information.service';
import {AbmsBoardScreensManager} from './abms/abms-board-screens-manager.service';
import {AbmsBoardSubspecialtyScreensManager} from './abms-subspecialty/abms-board-subspecialty-screens-manager.service';
import {AbpsBoardScreensManager} from './abps/abps-board-screens-manager.service';
import {ScreensManager, ScreensManagerDeps} from '../../../../components/sections/service/screen-manager.class';
import {Step} from '../../../../components/sections/service/step.class';
import {Screen} from '../../../../components/sections/service/screen.class';
import {AoaBoardScreensManager} from './aoa/aoa-board-screens-manager.service';
import {AoaBoardSubspecialtyScreensManager} from './aoa-subspecialty/aoa-board-subspecialty-screens-manager.service';

export enum BoardScreens {
    SELECTION = 1,
    ABMS,
    ABMS_SUBSPECIALTY,
    ABPS,
    AOA,
    AOA_SUBSPECIALTY,
    SPECIALTY_EXAMINATION
}

@Injectable()
export class BoardScreensManager extends ScreensManager {

    private _currentScreen$ = new ReplaySubject<Screen>(1);
    screenChange$: Observable<boolean> = this._currentScreen$.map(() => true).publishReplay(1).refCount();

    boardAffiliationInformation$ = new BehaviorSubject<BoardAffiliationInformation|undefined>(undefined);

    get boardAffiliationInformation(): BoardAffiliationInformation|undefined {
        return this.boardAffiliationInformation$.getValue();
    }

    set boardAffiliationInformation(value: BoardAffiliationInformation|undefined) {
        this.boardAffiliationInformation$.next(value);
    }

    constructor(smgm: ScreensManagerDeps,
                private readonly _boardAffiliationInformationService: BoardAffiliationInformationService,
                private readonly _abmsScreensManager: AbmsBoardScreensManager,
                private readonly _abmsSubspecialtyScreensManager: AbmsBoardSubspecialtyScreensManager,
                private readonly _abpsScreensManager: AbpsBoardScreensManager,
                private readonly _aoaScreensManager: AoaBoardScreensManager,
                private readonly _aoaSubspecialtyScreensManager: AoaBoardSubspecialtyScreensManager) {
        super(smgm);

        this._boardAffiliationInformationService.changes$.switchMap(() => this.refresh()).subscribe(() => {});

        this.refreshed$ = Observable.from([
            {refreshed$: this.refreshed$},
            this._abmsScreensManager, this._abmsSubspecialtyScreensManager,
            this._abpsScreensManager,
            this._aoaScreensManager, this._aoaSubspecialtyScreensManager
        ])
            .map(it => it.refreshed$).combineAll()
            .map(values => (values as Array<boolean>).every(it => it));
    }

    init(step: Step) {
        this._abmsScreensManager.init(step.idToScreenMap[BoardScreens.ABMS]);
        this._abmsSubspecialtyScreensManager.init(step.idToScreenMap[BoardScreens.ABMS_SUBSPECIALTY]);
        this._abpsScreensManager.init(step.idToScreenMap[BoardScreens.ABPS]);
        this._aoaScreensManager.init(step.idToScreenMap[BoardScreens.AOA]);
        this._aoaSubspecialtyScreensManager.init(step.idToScreenMap[BoardScreens.AOA_SUBSPECIALTY]);
        super.init(step);
    }

    setCurrentScreen(screen: Screen, route: ActivatedRouteSnapshot) {
        super.setCurrentScreen(screen, route);
        let screenId = +screen.id;
        if (screenId === BoardScreens.ABMS) this._abmsScreensManager.setCurrentScreen(screen, route.firstChild);
        if (screenId === BoardScreens.ABMS_SUBSPECIALTY) this._abmsSubspecialtyScreensManager.setCurrentScreen(screen, route.firstChild);
        if (screenId === BoardScreens.ABPS) this._abpsScreensManager.setCurrentScreen(screen, route.firstChild);
        if (screenId === BoardScreens.AOA) this._aoaScreensManager.setCurrentScreen(screen, route.firstChild);
        if (screenId === BoardScreens.AOA_SUBSPECIALTY) this._aoaSubspecialtyScreensManager.setCurrentScreen(screen, route.firstChild);
        this._currentScreen$.next(screen);
    }

    hasPreviousScreen(): boolean {
        return +this._currentScreen!.id > this.firstScreenId();
    }

    hasNextScreen(): boolean {
        let screenId = +this._currentScreen!.id;
        if (screenId === BoardScreens.SELECTION) return true;
        else if (screenId === BoardScreens.ABMS) return this._abmsScreensManager.hasNextScreen() || !!this.boardAffiliationInformation && (this.boardAffiliationInformation.hasAbps || this.boardAffiliationInformation.hasAoa);
        else if (screenId === BoardScreens.ABMS_SUBSPECIALTY) return true;
        else if (screenId === BoardScreens.ABPS) return this._abpsScreensManager.hasNextScreen() || !!this.boardAffiliationInformation && this.boardAffiliationInformation.hasAoa;
        else if (screenId === BoardScreens.AOA) return this._aoaScreensManager.hasNextScreen();
        else if (screenId === BoardScreens.AOA_SUBSPECIALTY) return true;
        else return false;
    }

    previousScreenId(): string|number|undefined {
        let screenId = +this._currentScreen!.id;
        if (screenId === BoardScreens.SPECIALTY_EXAMINATION
            || screenId === BoardScreens.AOA && !this._aoaScreensManager.hasPreviousScreen() && !this.boardAffiliationInformation!.hasAbps && !this.boardAffiliationInformation!.hasAbms
            || screenId === BoardScreens.ABPS && !this._abpsScreensManager.hasPreviousScreen() && !this.boardAffiliationInformation!.hasAbms
            || screenId === BoardScreens.ABMS && !this._abmsScreensManager.hasPreviousScreen()) {
            return BoardScreens.SELECTION;
        }
    }

    navigateToPreviousScreen(): Observable<boolean> {
        let screenId = +this._currentScreen!.id;
        if (screenId === BoardScreens.AOA_SUBSPECIALTY) {
            if (this._aoaSubspecialtyScreensManager.hasPreviousScreen()) return this._aoaSubspecialtyScreensManager.navigateToPreviousScreen();
            else return this._aoaScreensManager.navigateToScreen(this._aoaSubspecialtyScreensManager.aoaIndex);
        }
        else if (screenId === BoardScreens.AOA) {
            if (this._aoaScreensManager.hasPreviousScreen()) return this._aoaSubspecialtyScreensManager.navigateToLastScreenWithAoaIndex(this._aoaScreensManager.itemIndex - 1);
            else if (this.boardAffiliationInformation!.hasAbps) return this._abpsScreensManager.navigateToLastScreen();
            else return this._abmsScreensManager.navigateToLastScreen();
        }
        else if (screenId === BoardScreens.ABPS) {
            if (this._abpsScreensManager.hasPreviousScreen()) return this._abpsScreensManager.navigateToPreviousScreen();
            else return this._abmsScreensManager.navigateToLastScreen();
        }
        else if (screenId === BoardScreens.ABMS_SUBSPECIALTY) {
            if (this._abmsSubspecialtyScreensManager.hasPreviousScreen()) return this._abmsSubspecialtyScreensManager.navigateToPreviousScreen();
            else return this._abmsScreensManager.navigateToScreen(this._abmsSubspecialtyScreensManager.abmsIndex);
        }
        else if (screenId === BoardScreens.ABMS) return this._abmsSubspecialtyScreensManager.navigateToLastScreenWithAbmsIndex(this._abmsScreensManager.itemIndex - 1);
        else return Observable.of(true);
    }

    nextScreenId(): string|number|undefined {
        let screenId = +this._currentScreen!.id;
        if (screenId === BoardScreens.SELECTION && (!this.boardAffiliationInformation || (!this.boardAffiliationInformation.hasAbms && !this.boardAffiliationInformation.hasAbps && !this.boardAffiliationInformation.hasAoa))) {
            return BoardScreens.SPECIALTY_EXAMINATION;
        }
    }

    navigateToNextScreen(): Observable<boolean> {
        let screenId = +this._currentScreen!.id;
        if (screenId === BoardScreens.SELECTION) {
            if (this.boardAffiliationInformation!.hasAbms) return this._abmsScreensManager.navigateToFirstScreen();
            else if (this.boardAffiliationInformation!.hasAbps) return this._abpsScreensManager.navigateToFirstScreen();
            else return this._aoaScreensManager.navigateToFirstScreen();
        }
        else if (screenId === BoardScreens.ABMS) {
            if (this._abmsScreensManager.hasNextScreen()) return this._abmsSubspecialtyScreensManager.navigateToFirstScreenWithAbmsIndex(this._abmsScreensManager.itemIndex);
            else if (this.boardAffiliationInformation!.hasAbps) return this._abpsScreensManager.navigateToFirstScreen();
            else return this._aoaScreensManager.navigateToFirstScreen();
        }
        else if (screenId === BoardScreens.ABMS_SUBSPECIALTY) {
            if (this._abmsSubspecialtyScreensManager.hasNextScreen()) return this._abmsSubspecialtyScreensManager.navigateToNextScreen();
            else return this._abmsScreensManager.navigateToNextScreenFrom(this._abmsSubspecialtyScreensManager.abmsIndex);
        }
        else if (screenId === BoardScreens.ABPS) {
            if (this._abpsScreensManager.hasNextScreen()) return this._abpsScreensManager.navigateToNextScreen();
            else return this._aoaScreensManager.navigateToFirstScreen();
        }
        else if (screenId === BoardScreens.AOA) return this._aoaSubspecialtyScreensManager.navigateToFirstScreenWithAoaIndex(this._aoaScreensManager.itemIndex);
        else if (screenId === BoardScreens.AOA_SUBSPECIALTY) {
            if (this._aoaSubspecialtyScreensManager.hasNextScreen()) return this._aoaSubspecialtyScreensManager.navigateToNextScreen();
            else return this._aoaScreensManager.navigateToNextScreenFrom(this._aoaSubspecialtyScreensManager.aoaIndex);
        }
        else return Observable.of(true);
    }

    firstScreenId(): number {
        return BoardScreens.SELECTION;
    }

    navigateToFirstScreen(): Observable<boolean> {
        return Observable.of(true);
    }

    lastScreenId(): string|number|undefined {
        if (!this.boardAffiliationInformation || (!this.boardAffiliationInformation.hasAbms && !this.boardAffiliationInformation.hasAbps && !this.boardAffiliationInformation.hasAoa)) {
            return BoardScreens.SPECIALTY_EXAMINATION;
        }
    }

    navigateToLastScreen(): Observable<boolean> {
        if (this.boardAffiliationInformation!.hasAoa) return this._aoaScreensManager.navigateToLastScreen();
        if (this.boardAffiliationInformation!.hasAbps) return this._abpsScreensManager.navigateToLastScreen();
        else return this._abmsScreensManager.navigateToLastScreen();
    }

    protected _doRefresh(): Observable<any> {
        return this._boardAffiliationInformationService.getCurrentUserModel().do(value => this.boardAffiliationInformation = value);
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/pages/licenses-certifications/steps/board/board-screens-manager.service.ts